import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import { useMemo } from 'react';

import { type SectorType } from '@/api/common';
import { useSectors } from '@/api/settings';
import {
  Combobox,
  ComboboxChip,
  ComboboxChips,
  ComboboxChipsInput,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxItem,
  ComboboxList,
  ComboboxValue,
  type ComboboxProps,
} from '@/components/Form/pickers';

type Props = ComboboxProps<SectorType, true> & {
  disabled?: boolean;
  className?: string;
  value?: SectorType[];
};

const IndustryMultiSelect = ({
  value,
  size,
  disabled,
  className,
  'aria-invalid': ariaInvalid,
  ...rest
}: Props) => {
  const { sectors, isLoading } = useSectors();
  const sectorsById = useMemo(
    () => new Map((sectors ?? []).map((sector) => [sector.id, sector])),
    [sectors],
  );
  const resolvedValue = useMemo(() => {
    if (!value?.length) return [];
    return value
      .map((sector) => sectorsById.get(sector.id) ?? sector)
      .filter((sector) => Boolean(sector.name));
  }, [sectorsById, value]);
  return (
    <Combobox
      data-testid="industry-multiselect"
      {...rest}
      multiple
      items={isLoading ? [] : (sectors ?? [])}
      itemToStringValue={(item) => item.id}
      itemToStringLabel={(item) => item.name ?? ''}
      isItemEqualToValue={(item, selected) => item.id === selected.id}
      isLoading={isLoading}
      disabled={disabled}
      size={size}
      value={resolvedValue}
    >
      <ComboboxChips className={className} aria-invalid={ariaInvalid}>
        <ComboboxValue>
          {resolvedValue.map((item) => (
            <ComboboxChip key={item.id}>{item.name}</ComboboxChip>
          ))}
        </ComboboxValue>
        <ComboboxChipsInput
          placeholder={isLoading ? t`Loading Industries...` : t`Select Industries...`}
        />
      </ComboboxChips>
      <ComboboxContent>
        <ComboboxEmpty>
          <Trans>No industries were found...</Trans>
        </ComboboxEmpty>
        <ComboboxList>
          {(item: SectorType) => (
            <ComboboxItem key={item.id} value={item} disabled={item.disabled}>
              {item.name}
            </ComboboxItem>
          )}
        </ComboboxList>
      </ComboboxContent>
    </Combobox>
  );
};

export default IndustryMultiSelect;
